import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import PageService from 'services/PageService/PageService';
import { navigationPaths, PAGE_INVALIDATION_INTERVAL } from 'data/constants';
import GlobalLayout from '../../blocks/Layouts/GlobalLayout';
import Article from '../../blocks/travel-journal-articles/article';
import type { IGlobalLayout } from 'types/interfaces';
import type {
  TravelJournalArticle,
  TravelJournalArticlesType,
  TravelJournalCategoryType,
  HeadMetaDataType,
} from 'types/types';
import HomePageService from 'services/HomePageService/HomePageService';
import { ContentType } from 'components/Carrousel';
import { InstagramCarrousel } from 'blocks/Carrousel';
import Category from 'blocks/travel-journal-articles/category';

type ArticleProps = {
  globalLayoutProps: IGlobalLayout;
  article: TravelJournalArticle;
  instagram: ContentType[];
  category?: TravelJournalCategoryType;
  categories?: TravelJournalCategoryType[];
  isCategory: boolean;
  articles: TravelJournalArticlesType[];
};

const ArticlePage: NextPage<ArticleProps> = ({
  globalLayoutProps,
  article,
  instagram,
  category,
  categories,
  isCategory,
  articles,
}) => {
  const router = useRouter();
  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  return (
    <GlobalLayout {...globalLayoutProps}>
      {isCategory ? (
        <Category
          category={category!}
          categories={categories!}
          articles={articles}
        />
      ) : (
        <>
          <Article article={article!} />
          <div className="outOfBounds">
            <InstagramCarrousel instagramData={instagram!} />
          </div>
        </>
      )}
    </GlobalLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string;
  const genericPageProps = await PageService.getGenericPageProps({
    navPath: navigationPaths.TRAVEL_JOURNAL_ARTICLE,
    title: '',
    description: '',
  });

  const { articles } = await PageService.getTravelJournalArticlesPage();
  const { categories } = await PageService.getTravelJournalCategories();

  const matchedCategory = categories.find(category => category.slug === slug);

  if (matchedCategory) {
    const { category } = await PageService.getTravelJournalCategory(slug);

    const categoryMetadata: HeadMetaDataType = {
      title: `Travel Journal - ${matchedCategory.name} | Little Emperors`,
      description: `Travel Journal - ${matchedCategory.name}`,
      url: `${process.env.NEXT_PUBLIC_CLIENT_ENDPOINT}/travel-journal/${slug}`,
      type: 'category',
      image: matchedCategory.image || undefined,
    };

    return {
      props: {
        globalLayoutProps: {
          ...genericPageProps.globalLayoutProps,
          metaData: categoryMetadata,
        },
        category,
        categories,
        isCategory: true,
        articles,
        article: null,
        instagram: null,
      },
      revalidate: PAGE_INVALIDATION_INTERVAL,
    };
  }

  const article = await PageService.getArticleBySlug(slug);
  if (!article) {
    return { notFound: true };
  }

  const instagram = await HomePageService.getInstagramImages().then(data =>
    data.map(item => ({
      id: item.id,
      image: item.url,
      url: item.permalink,
    }))
  );

  const articleMetadata: HeadMetaDataType = {
    title: `${article.title} | Little Emperors`,
    description: article.introduction || article.subheading,
    url: `${process.env.NEXT_PUBLIC_CLIENT_ENDPOINT}/travel-journal/${slug}`,
    type: 'article',
    ...(article.header_image?.image && { image: article.header_image.image }),
    ...(article.author && { author: article.author }),
    ...(article.date && { publishedTime: article.date }),
  };

  return {
    props: {
      globalLayoutProps: {
        ...genericPageProps.globalLayoutProps,
        metaData: articleMetadata,
      },
      article,
      instagram,
      isCategory: false,
      articles,
      categories,
      category: null,
    },
    revalidate: PAGE_INVALIDATION_INTERVAL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { articles } = await PageService.getTravelJournalArticlesPage();
  const { categories } = await PageService.getTravelJournalCategories();

  const articlePaths = articles.map((article: TravelJournalArticlesType) => ({
    params: { slug: article.slug },
  }));

  const categoryPaths = categories.map(
    (category: TravelJournalCategoryType) => ({
      params: { slug: category.slug },
    })
  );

  const paths = [...articlePaths, ...categoryPaths];

  return {
    paths,
    fallback: true,
  };
};

export default ArticlePage;
